import React, { useEffect, useState } from "react";
import axios from "../api/axiosInstance";
import {
  FaStar,
  FaEdit,
  FaTrash,
  FaIdCard,
  FaPlus,
  FaTimes,
  FaInbox,
} from "react-icons/fa";
import { Link } from "react-router-dom";

const PlanCardSkeleton = () => (
  <div className="rounded-2xl border border-[#EFE3D0] bg-white p-5 sm:p-6 animate-pulse space-y-3">
    <div className="h-4 w-2/3 rounded bg-[#F3EADC]" />
    <div className="h-3 w-1/3 rounded bg-[#F3EADC]" />
    <div className="h-8 w-1/2 rounded bg-[#F3EADC] mt-2" />
    <div className="h-3 w-full rounded bg-[#F3EADC] mt-4" />
    <div className="flex gap-2 mt-4">
      <div className="h-9 flex-1 rounded-lg bg-[#F3EADC]" />
      <div className="h-9 flex-1 rounded-lg bg-[#F3EADC]" />
    </div>
  </div>
);

const MembershipPlan = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editPlan, setEditPlan] = useState(null); // For edit modal
  const [formData, setFormData] = useState({
    name: "",
    duration_months: "",
    profile_access: "",
    original_price: "",
    discounted_price: "",
    save_percentage: "",
    best_selling: false,
  });

  const fetchPlans = async () => {
    try {
      const response = await axios.get("/api/memberships/membership-plans");
      setPlans(response.data);
    } catch (error) {
      console.error("Error fetching membership plans:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleEdit = (plan) => {
    setEditPlan(plan);
    setFormData({
      name: plan.name,
      duration_months: plan.duration_months,
      profile_access: plan.profile_access,
      original_price: plan.original_price,
      discounted_price: plan.discounted_price,
      save_percentage: plan.save_percentage,
      best_selling: plan.best_selling,
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleUpdate = async () => {
    try {
      await axios.put(
        `/api/memberships/membership-plans/${editPlan.id}/`,
        formData,
      );
      setEditPlan(null);
      fetchPlans(); // Refresh data
    } catch (error) {
      console.error("Update failed:", error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this plan?")) return;
    try {
      await axios.delete(`/api/memberships/membership-plans/${id}/`);
      setPlans(plans.filter((plan) => plan.id !== id));
    } catch (error) {
      console.error("Failed to delete:", error);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-start sm:items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-[#4A2E1E]">
            Membership Plans
          </h2>
          <div className="mt-2 h-1 w-14 rounded-full bg-gradient-to-r from-[#8B1E3F] to-[#C9A227]" />
        </div>
        <Link to="/admin-html-secure-metro/planform">
          <button className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-[#8B1E3F] to-[#6E1228] hover:opacity-90 transition-opacity shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A227]">
            <FaPlus size={12} /> Add Plan
          </button>
        </Link>
      </div>

      {/* Plan cards */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <PlanCardSkeleton />
          <PlanCardSkeleton />
          <PlanCardSkeleton />
        </div>
      ) : plans.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-16 text-[#A6998C] rounded-2xl border border-dashed border-[#EFE3D0] bg-white">
          <FaInbox size={22} />
          <p className="text-sm">No membership plans yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {plans.map((plan) => {
            const hasDiscount =
              plan.original_price &&
              Number(plan.original_price) > Number(plan.discounted_price);
            const months = Number(plan.duration_months);

            return (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded-2xl border bg-white p-5 sm:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${
                  plan.best_selling
                    ? "border-[#C9A227] shadow-[0_12px_28px_-14px_rgba(184,134,11,0.45)]"
                    : "border-[#EFE3D0] shadow-sm"
                }`}
              >
                {plan.best_selling && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-semibold text-white bg-gradient-to-r from-[#C9A227] to-[#8B1E3F] shadow-sm whitespace-nowrap">
                    <FaStar size={10} /> Most Popular
                  </span>
                )}

                <h3 className="text-lg font-bold text-[#4A2E1E] mt-2 truncate">
                  {plan.name}
                </h3>
                <p className="text-xs text-[#A6998C] mb-4">
                  {months} month{months === 1 ? "" : "s"}
                </p>

                <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                  <span className="text-2xl sm:text-3xl font-bold text-[#8B1E3F] tabular-nums">
                    ₹
                    {Number(plan.discounted_price || 0).toLocaleString("en-IN")}
                  </span>
                  {hasDiscount && (
                    <span className="text-sm text-[#A6998C] line-through tabular-nums">
                      ₹{Number(plan.original_price).toLocaleString("en-IN")}
                    </span>
                  )}
                </div>

                <div className="mb-4 min-h-[22px]">
                  {plan.save_percentage ? (
                    <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#E8F3EA] text-[#2F6B3A]">
                      Save {plan.save_percentage}%
                    </span>
                  ) : null}
                </div>

                <div className="flex items-center gap-2 text-sm text-[#6B5E52] py-3 border-t border-[#F3EADC]">
                  <FaIdCard className="text-[#B8860B] shrink-0" size={14} />
                  <span>Access to {plan.profile_access} profiles</span>
                </div>

                <div className="flex items-center gap-2 mt-1 pt-4 border-t border-[#F3EADC]">
                  <button
                    onClick={() => handleEdit(plan)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-[#E9C3CD] text-[#8B1E3F] text-sm font-medium hover:bg-[#FBEFF2] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A227]"
                  >
                    <FaEdit size={13} /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(plan.id)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-[#F3D9DE] text-[#B3273F] text-sm font-medium hover:bg-[#FBEFF2] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A227]"
                  >
                    <FaTrash size={12} /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit modal */}
      {editPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#3A2417]/30 backdrop-blur-sm px-3">
          <div className="w-full max-w-md bg-white p-5 sm:p-6 rounded-2xl shadow-2xl border border-[#EFE3D0] max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-[#4A2E1E]">
                Edit Plan
              </h3>
              <button
                onClick={() => setEditPlan(null)}
                aria-label="Close"
                className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-[#EFE3D0] text-[#6B5E52] hover:bg-[#F4F1ED] transition-colors"
              >
                <FaTimes size={13} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block mb-1 text-sm font-medium text-[#6B5E52]">
                  Plan Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full border border-[#EFE3D0] px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                />
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-[#6B5E52]">
                  Duration (months)
                </label>
                <input
                  type="number"
                  name="duration_months"
                  value={formData.duration_months}
                  onChange={handleChange}
                  className="w-full border border-[#EFE3D0] px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block mb-1 text-sm font-medium text-[#6B5E52]">
                    Original Price
                  </label>
                  <input
                    type="number"
                    name="original_price"
                    value={formData.original_price}
                    onChange={handleChange}
                    className="w-full border border-[#EFE3D0] px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-[#6B5E52]">
                    Discounted Price
                  </label>
                  <input
                    type="number"
                    name="discounted_price"
                    value={formData.discounted_price}
                    onChange={handleChange}
                    className="w-full border border-[#EFE3D0] px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block mb-1 text-sm font-medium text-[#6B5E52]">
                    Save %
                  </label>
                  <input
                    type="number"
                    name="save_percentage"
                    value={formData.save_percentage}
                    onChange={handleChange}
                    className="w-full border border-[#EFE3D0] px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-[#6B5E52]">
                    Profile Access
                  </label>
                  <input
                    type="number"
                    name="profile_access"
                    value={formData.profile_access}
                    onChange={handleChange}
                    className="w-full border border-[#EFE3D0] px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-[#6B5E52] cursor-pointer">
                <input
                  type="checkbox"
                  name="best_selling"
                  checked={formData.best_selling}
                  onChange={handleChange}
                  className="w-4 h-4 rounded border-[#EFE3D0] text-[#8B1E3F] focus:ring-[#C9A227]"
                />
                <span>Mark as Best Selling</span>
              </label>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-[#F3EADC]">
              <button
                onClick={() => setEditPlan(null)}
                className="px-5 py-2 rounded-lg text-sm font-medium border border-[#EFE3D0] text-[#6B5E52] hover:bg-[#F4F1ED] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className="px-5 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-[#8B1E3F] to-[#6E1228] hover:opacity-90 transition-opacity shadow-sm"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipPlan;
